// Essai d'un scénario d'investissement proposé (API 0.11.0, POST /v0/cartes/{id}/variantes/essai) : ajouter ou retirer
// un geste, supposer un passage réseau ouvert ou fermé, et voir les chiffres RECALCULÉS par le moteur, comparés au
// scénario tel qu'il est. Rien n'est écrit : ni la carte, ni le journal, ni une fiche de décision. L'application ne
// calcule aucun chiffre ; elle envoie l'essai et met en forme la réponse. Vocabulaire : docs/REGLE-EDITORIALE.md.
import { ReserveCalcul } from './ManquesCalcul';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { CalculEssai, DemandeEssai, EssaiVariante, Lien, Variante } from '../api/types';
import { useCarte, useEkera } from '../contexte';
import { decimal, eurosCourt, eurosLong, fourchette, pourcent } from '../domaine/format';
import { lienVers } from '../routeur';

type Fourchette = { bas?: { valeur: number } | null; haut?: { valeur: number } | null } | null | undefined;

function plage(f: Fourchette): string {
  return f?.bas && f?.haut ? fourchette(f.bas.valeur, f.haut.valeur) : '—';
}

function signe(v: number, texte: string): string {
  if (Math.abs(v) < 1e-9) return 'inchangé';
  return `${v > 0 ? '+' : '−'}${texte}`;
}

function ecartEuros(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : signe(v, eurosLong(Math.abs(v)));
}

function ecartPoints(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : signe(v, `${Math.round(Math.abs(v) * 100)} points`);
}

function ecartFourchette(f: { bas?: number | null; haut?: number | null } | undefined): string {
  if (!f || f.bas === null || f.bas === undefined || f.haut === null || f.haut === undefined) return '—';
  if (Math.abs(f.bas) < 1e-9 && Math.abs(f.haut) < 1e-9) return 'inchangé';
  const s = (x: number) => `${x > 0 ? '+' : '−'}${eurosCourt(Math.abs(x))}`;
  return `${s(f.bas)} à ${s(f.haut)}`;
}

/** Les passages réseau ouverts de la carte, ceux à confirmer (lus, pas encore validés) en premier. */
function passagesOuverts(liens: Lien[]): Lien[] {
  return liens
    .filter((l) => l.type === 'passage_reseau' && !l.retire && l.attributs?.ouvert !== false && !l.attributs?.essentiel)
    .sort((a, b) => Number(b.provenance?.statut === 'propose') - Number(a.provenance?.statut === 'propose'));
}

export function PanneauEssai({ variante, surFermer }: { variante: Variante; surFermer: () => void }) {
  const { client, cid } = useEkera();
  const carte = useCarte();
  const [ajoutes, setAjoutes] = useState<string[]>([]);
  const [retires, setRetires] = useState<string[]>([]);
  const [fermes, setFermes] = useState<string[]>([]);
  const demande: DemandeEssai = {
    variante: variante.id,
    ajouter: ajoutes,
    retirer: retires,
    hypotheses: fermes.map((passage) => ({ passage, actif: false })),
  };
  const essai = useQuery<EssaiVariante>({
    queryKey: ['carte', cid, 'essai', variante.id, ajoutes, retires, fermes],
    queryFn: () => client.essayerVariante(cid, demande),
    staleTime: Infinity,
    retry: false,
    placeholderData: (precedent) => precedent,
  });
  const noms = useMemo(() => new Map((carte.data?.objets ?? []).map((o) => [o.id, o.nom])), [carte.data]);
  const passages = useMemo(() => passagesOuverts(carte.data?.liens ?? []), [carte.data]);
  const dansLeScenario = new Set((variante.gestes ?? []).map((g) => g.id));
  const r = essai.data;
  const basculer = (liste: string[], fixer: (l: string[]) => void, id: string) =>
    fixer(liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id]);
  // en vue : les gestes du scénario, ceux ajoutés, et le filtrage des passages (le geste qui complète un scénario)
  const enVue = (id: string) => dansLeScenario.has(id) || ajoutes.includes(id) || id.startsWith('g-link-');
  const caseGeste = (g: NonNullable<EssaiVariante['catalogue']>[number]) => {
    const dedans = dansLeScenario.has(g.id);
    const coche = dedans ? !retires.includes(g.id) : ajoutes.includes(g.id);
    return (
      <li key={g.id}>
        <label>
          <input
            type="checkbox"
            checked={coche}
            onChange={() => (dedans ? basculer(retires, setRetires, g.id) : basculer(ajoutes, setAjoutes, g.id))}
            data-testid={`geste-${g.id}`}
          />{' '}
          {g.libelle ?? g.id}{' '}
          <small className="num">
            {g.mise_en_place ? eurosLong(g.mise_en_place.valeur) : '—'}
            {g.annuel && g.annuel.valeur > 0 ? ` + ${eurosLong(g.annuel.valeur)} par an` : ''}
            {dedans ? ' · dans le scénario proposé' : ''}
          </small>
        </label>
      </li>
    );
  };

  return (
    <section className="panneau essai" aria-labelledby={`t-essai-${variante.id}`} data-testid="essai">
      <div className="panneau-tete">
        <h3 id={`t-essai-${variante.id}`}>
          Essai du scénario {variante.budget ? `à ${eurosCourt(variante.budget.valeur)}` : variante.nom}
        </h3>
        <button type="button" className="btn petit" onClick={surFermer}>
          Fermer l’essai
        </button>
      </div>
      <p className="bandeau-demo" role="status" data-testid="essai-rien-ecrit">
        <b>Essai : rien n’est écrit.</b> La carte, le journal et les fiches de décision restent tels qu’ils sont ; les
        chiffres sont recalculés par le moteur pour cet essai seulement.
      </p>

      {essai.isError && (
        <p role="alert">L’essai n’a pas pu être calculé : {essai.error instanceof Error ? essai.error.message : 'erreur'}.</p>
      )}
      {r && <ResultatEssai r={r} enCours={essai.isFetching} nom={(id) => noms.get(id) ?? id} />}

      <div className="grille g2">
        <fieldset>
          <legend>Gestes</legend>
          <ul className="liste-points">{(r?.catalogue ?? []).filter((g) => enVue(g.id)).map(caseGeste)}</ul>
          <details>
            <summary>Autres gestes du catalogue</summary>
            <ul className="liste-points">{(r?.catalogue ?? []).filter((g) => !enVue(g.id)).map(caseGeste)}</ul>
          </details>
        </fieldset>
        <fieldset>
          <legend>Passages réseau : points à confirmer</legend>
          {passages.length === 0 && <p className="note">Aucun passage ouvert sur la carte.</p>}
          <ul className="liste-points">
            {passages.map((l) => {
              const ferme = fermes.includes(l.id);
              const nom = `${noms.get(l.source) ?? l.source} ${l.attributs?.sens_unique ? '→' : '↔'} ${noms.get(l.cible) ?? l.cible}`;
              return (
                <li key={l.id} data-testid={`passage-${l.id}`}>
                  <b>{nom}</b>
                  {l.provenance?.statut === 'propose' && <span className="pastille">à confirmer par le DSI</span>}
                  {l.attributs?.motif && <small style={{ display: 'block' }}>{l.attributs.motif}</small>}
                  <span role="radiogroup" style={{ display: 'block' }} aria-label={`Hypothèse sur le passage ${nom}`}>
                    <label>
                      <input type="radio" name={`h-${l.id}`} checked={!ferme} onChange={() => setFermes(fermes.filter((x) => x !== l.id))} />{' '}
                      ouvert, tel que lu
                    </label>{' '}
                    <label>
                      <input
                        type="radio"
                        name={`h-${l.id}`}
                        checked={ferme}
                        onChange={() => setFermes([...fermes, l.id])}
                        data-testid={`fermer-${l.id}`}
                      />{' '}
                      fermé (hypothèse)
                    </label>
                  </span>
                  <a style={{ display: 'block' }} href={lienVers('corrections', { element: l.id })}>
                    Faire confirmer ce passage par le DSI
                  </a>
                </li>
              );
            })}
          </ul>
        </fieldset>
      </div>

    </section>
  );
}

function ligne(libelle: string, origine: string, essai: string, ecart: string, id: string) {
  return (
    <tr key={id} data-testid={`essai-${id}`}>
      <th scope="row">{libelle}</th>
      <td className="num">{origine}</td>
      <td className="num">{essai}</td>
      <td className="num">{ecart}</td>
    </tr>
  );
}

function ResultatEssai({ r, enCours, nom }: { r: EssaiVariante; enCours: boolean; nom: (id: string) => string }) {
  const o: CalculEssai = r.origine;
  const e: CalculEssai = r.essai;
  const d = r.difference;
  const h = r.horizon;
  const rayon = (c: CalculEssai) => (c.apres.rayon_max === null || c.apres.rayon_max === undefined ? '—' : pourcent(c.apres.rayon_max));
  const nis2 = (c: CalculEssai) => (c.apres.score_nis2 === null || c.apres.score_nis2 === undefined ? '—' : `${decimal(c.apres.score_nis2)} / 10`);
  return (
    <div aria-busy={enCours} data-testid="essai-resultat">
      <ReserveCalcul manques={r.manques} nom={nom} />
      <div className="tableau-defile">
        <table>
          <caption>Scénario proposé et essai, chiffrés par le moteur sur la même carte</caption>
          <thead>
            <tr>
              <th scope="col">Sur {h} ans, selon les hypothèses retenues</th>
              <th scope="col" className="num">
                Scénario proposé
              </th>
              <th scope="col" className="num">
                Essai
              </th>
              <th scope="col" className="num">
                Écart
              </th>
            </tr>
          </thead>
          <tbody>
            {ligne('Coût', o.cout.total ? eurosLong(o.cout.total.valeur) : '—', e.cout.total ? eurosLong(e.cout.total.valeur) : '—', ecartEuros(d.cout_total), 'cout')}
            {ligne('Pire arrêt (part de l’activité critique)', rayon(o), rayon(e), ecartPoints(d.rayon_max), 'rayon')}
            {ligne(
              'Pertes potentielles estimées, par an',
              plage(o.apres.perte_annuelle_attendue),
              plage(e.apres.perte_annuelle_attendue),
              ecartFourchette(d.perte_annuelle_attendue),
              'pertes',
            )}
            {ligne('Pertes potentielles évitées', plage(o.perte_evitee), plage(e.perte_evitee), '—', 'evitees')}
            {ligne('Indicateur de progression NIS2', nis2(o), nis2(e), d.score_nis2 ? signe(d.score_nis2, decimal(Math.abs(d.score_nis2))) : 'inchangé', 'nis2')}
          </tbody>
        </table>
      </div>
      {(r.hypotheses?.length ?? 0) > 0 && (
        <p className="note" data-testid="essai-hypotheses">
          Hypothèse de l’essai : {(r.hypotheses ?? []).map((x) => `passage supposé ${x.actif ? 'ouvert' : 'fermé'}`).join(', ')} ;
          situation de départ sous cette hypothèse : pire arrêt {rayon({ ...e, apres: e.avant })}. La carte n’est pas
          modifiée : le passage reste un point à confirmer avant de prendre une décision.
        </p>
      )}
      {(e.gestes_sans_objet?.length ?? 0) > 0 && (
        <p className="note">
          Sans objet dans cette hypothèse, donc non compté : {e.gestes_sans_objet?.length} geste
          {(e.gestes_sans_objet?.length ?? 0) > 1 ? 's' : ''} (filtrage d’un passage supposé fermé).
        </p>
      )}
      <p className="note">
        Pertes potentielles estimées, conditionnelles aux hypothèses retenues (calibration provisoire, non validée par
        un actuaire) : ce sont des chiffres calculés par le moteur, pas des pertes mesurées. Le scénario
        d’investissement proposé reste à valider par l’entreprise.
      </p>
    </div>
  );
}
